import queryString, { ParsedQuery } from 'query-string';
import { Buffer } from 'buffer';

const env = process.env.GATSBY_ENV || process.env.NODE_ENV!;

export const getUrlWithTrailingSlash = (url: string) => {
  const parsedUrl = queryString.parseUrl(url, { parseFragmentIdentifier: true });
  return queryString.stringifyUrl({ ...parsedUrl, url: parsedUrl.url.endsWith('/') ? parsedUrl.url : parsedUrl.url + '/' });
};

const absoluteUrlPattern = /^https?:\/\//i;
const withjoyDotComUrlPattern = /^https:\/\/(.*\.withjoy.com)|(withjoy.com)\//i;
const demoEventHandleMap: { readonly [key in string]: string } = {
  local: 'amyplusdave',
  stage: 'amyplusdave',
  development: 'amyplusdave',
  production: 'example',
};

const DEMO_EVENT_HANDLE = demoEventHandleMap[env] || demoEventHandleMap['production'];
const BLANK_TEMPLATE_THEME_ID = 'blank';

export const isExternalUrl = (url: string | undefined) => {
  return url && absoluteUrlPattern.test(url) && !withjoyDotComUrlPattern.test(url);
};

const externalRootUrlsMap: { readonly [key in string]: string } = {
  stage: 'https://rehearsal.withjoy.com',
  development: 'https://dev.withjoy.com',
  production: 'https://withjoy.com',
};

const internalRootUrlsMap: { readonly [key in string]: string } = {
  local: 'localhost:8000',
  stage: 'https://rehearsal.withjoy.com',
  development: 'https://dev.withjoy.com',
  production: 'https://withjoy.com',
};

const blissGatewayUrlMap: { readonly [key in string]: string } = {
  local: 'https://dev.withjoy.com', // used for local development
  stage: 'https://rehearsal.withjoy.com',
  development: 'https://dev.withjoy.com',
  production: 'https://withjoy.com',
};

/**
 * get root url for non-marcom app, based on current marcom deployment env
 */
export const EXT_APP_ROOT_URL = externalRootUrlsMap[env] || externalRootUrlsMap['production'];

/**
 * get root url for internal marcom routes, based on current marcom deployment env
 */
export const INTERNAL_ROOT_URL = internalRootUrlsMap[env] || internalRootUrlsMap['production'];

/**
 * get root url for bliss gateway endpoints
 */
export const BLISS_GATEWAY_ROOT_URL = blissGatewayUrlMap[env] || internalRootUrlsMap['production'];

/**
 * Take url path: /login, /createwedding or /wedding-website and send to appropriate internal or external root url.
 */
export function getRootUrl(urlPath: string): string {
  const externalPaths: { readonly [key in string]: boolean } = {
    '/createwedding': true,
    '/createwedding/sign-in': true,
    '/login': true,
    '/amyplusdave': true,
    '/designs': true,
    '/event/edit/card': true,
    '/cards': true,
  };

  if (externalPaths[urlPath]) {
    return EXT_APP_ROOT_URL + urlPath;
  }

  return INTERNAL_ROOT_URL + urlPath;
}

/**
 * Get url according to the env and build (useful to run e2e test local/CI)
 */
export const getRootBuildUrl = () => {
  const query = { 'feature.marcom': process.env.SHORT_SHA };
  return queryString.stringifyUrl({ url: INTERNAL_ROOT_URL, query });
};

const baseCreateWeddingUrl = getRootUrl('/createwedding');

const pageToScenarioMap: { [key in string]: SignUpScenario } = {
  'wedding-invitations': 'invitation',
};

// TODO: move all createWedding to the generic getCreateEventUrl
export const getCreateWeddingUrl = (fromPage: SignUpScenario | undefined) => {
  const resultUrl = queryString.parseUrl(baseCreateWeddingUrl);
  const queryParams = resultUrl.query;
  if (fromPage && fromPage !== 'home') {
    queryParams.scenario = pageToScenarioMap[fromPage] ?? fromPage;
  }
  return queryString.stringifyUrl(resultUrl);
};

export const getCreateEventUrl = (fromPage: MarcomPageName | undefined) => {
  const page = fromPage === 'baby-registry' ? 'createbabyregistry' : 'createwedding';
  const resultUrl = queryString.parseUrl(getRootUrl(`/${page}`));
  const queryParams = resultUrl.query;
  if (fromPage && fromPage !== 'home') {
    queryParams.scenario = pageToScenarioMap[fromPage] ?? fromPage;
  }
  return queryString.stringifyUrl(resultUrl);
};

export const getLoginUrl = (fromPage: MarcomPageName | undefined) => {
  if (fromPage === 'baby-registry') {
    return getRootUrl('/login');
  }
  return getRootUrl('/createwedding/sign-in');
};

export const getCardCategoryFromVariationType = (variationType: string) => {
  const map: { readonly [key in string]: string } = {
    invite: 'invitation',
    savethedate: 'saveTheDate',
  };

  return map[variationType] || '';
};

const VARIATION_TYPE_TO_NAMED_CATEGORY_PATH: Record<'savethedate' | 'invite', string> = {
  savethedate: 'save-the-date',
  invite: 'invitation',
};

const getBase64EncodedWebsiteDesignSettings = (websiteSettings: GalleryECard['websiteSettings']) => {
  try {
    return Buffer.from(JSON.stringify(websiteSettings)).toString('base64');
  } catch (e) {
    console.error('Error converting website settings to base64', e);
  }
};

export const getGalleryCreateWeddingUrl = (
  cardDetails: GalleryECard,
  scenario: SignUpScenario,
  template: string,
  theme: string,
  font?: string,
  startWithCtaRedirectUrlPath?: string
) => {
  if ((cardDetails.variationType === 'savethedate' || cardDetails.variationType === 'invite') && cardDetails.isJoyPrintable) {
    const resultUrl = queryString.parseUrl(
      EXT_APP_ROOT_URL + `/cards/wedding/${VARIATION_TYPE_TO_NAMED_CATEGORY_PATH[cardDetails.variationType]}/product/${theme}`
    );

    resultUrl.query.scenario = scenario;
    return queryString.stringifyUrl(resultUrl);
  }

  const resultUrl = queryString.parseUrl(startWithCtaRedirectUrlPath ? getRootUrl(startWithCtaRedirectUrlPath) : baseCreateWeddingUrl);
  const queryParams = resultUrl.query;
  queryParams.scenario = scenario;
  queryParams.template = template;
  queryParams.theme = cardDetails.websiteSettings?.isBlankTemplate ? BLANK_TEMPLATE_THEME_ID : theme;

  if (font) {
    queryParams.font = font;
  }
  cardDetails.designThemeId && (queryParams.designThemeId = cardDetails.designThemeId);
  if (cardDetails.websiteSettings && Object.keys(cardDetails.websiteSettings).length > 0) {
    const base64EncodedWebsiteDesignSettings = getBase64EncodedWebsiteDesignSettings(cardDetails.websiteSettings);
    base64EncodedWebsiteDesignSettings && (queryParams.wds = base64EncodedWebsiteDesignSettings);
  }
  return queryString.stringifyUrl(resultUrl);
};

export const getGuestSiteUrl = (selectedVariant: GalleryECard, themeId: string, currentQueryParams: ParsedQuery<string | boolean>) => {
  const { variationType: layout, font } = selectedVariant;
  const { colors, pageTransparency, pageShadow, photoPresentationLayout, isBlankTemplate } = selectedVariant.websiteSettings || {};
  const { artBackground, pageBackground, pageFont, artFont, linkAndButton } = colors || {};
  const resultUrl = queryString.parseUrl(EXT_APP_ROOT_URL + `/${DEMO_EVENT_HANDLE}/welcome`);
  const queryParams = resultUrl.query;
  themeId && (queryParams.theme = isBlankTemplate ? BLANK_TEMPLATE_THEME_ID : themeId);
  layout && (queryParams.layout = layout);
  font && (queryParams.font = font);
  artBackground && (queryParams.bc = artBackground);
  pageBackground && (queryParams.pc = pageBackground);
  pageFont && (queryParams.pfc = pageFont);
  artFont && (queryParams.tc = artFont);
  linkAndButton && (queryParams.lc = linkAndButton);
  pageTransparency !== undefined && (queryParams.pt = String(pageTransparency));
  pageShadow !== undefined && (queryParams.ps = String(pageShadow));
  photoPresentationLayout && (queryParams.pl = photoPresentationLayout);
  if (Object.keys(currentQueryParams).length > 0) {
    Object.keys(currentQueryParams).forEach((key) => {
      queryParams[key] = currentQueryParams[key] as string;
    });
  }
  return queryString.stringifyUrl(resultUrl);
};
